import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './ServiceRequestDetailsModal.css';
import {
  formatInstantForViewer,
  getViewerTimezone,
  instantToWallClockInZone,
  toUtcInstant,
} from '../../utils/dateTime';
import { getRequesterReassignState } from '../../utils/serviceRequestPermissions';
import { getServiceTypeLabel } from '../../utils/serviceTypeLabel';

/**
 * ServiceRequestDetailsModal
 *
 * Read-only snapshot + inline edit of a linked service request. Opened
 * from the "View Details" button on a timeline row. Mirrors the mobile
 * ServiceRequestDetailsModal so requesters see identical content on both
 * platforms.
 *
 * Content follows the design reference: title, meta line, a three-card
 * amount grid (Estimated / Billed / Final Payment), and a Request Details
 * table. Estimated Amount reads sr.estimatedAmount directly; the other
 * two cards render an empty-state placeholder today — the backend fields
 * that back them (billedAmount from a provider-invoice pipeline,
 * finalPaymentAmount from an agent-recordable PATCH) haven't shipped yet.
 *
 * Edit flow: the Edit button toggles the modal into an inline-edit mode
 * where Scheduled, Urgency, Notes, and Estimated Amount become inputs.
 * Save calls the caller-provided onSave with a PATCH payload (only
 * changed fields — null keys leave the server-side value untouched).
 * Backend `PATCH /users/me/sent-service-requests/{id}` still gates edits
 * on PENDING / AWAITING_ASSIGNMENT, so the Edit button follows the same
 * !POST_ACCEPT_STATUSES rule the Assign/Reassign button already uses.
 *
 * Assign / Reassign live here too (moved off the timeline row); details
 * modal stays the single mutation entry point for the SR so we don't
 * re-open the cross-fire class of bugs the timeline had before.
 */

// Read-side formatters route through the shared viewer-zone helpers so the
// modal renders the same wall-clock the transaction timeline shows for the
// same SR instant. Without this, a viewer whose device is in one zone (say
// IST) and profile is in another (say PDT, from CA state) sees a Scheduled
// row in device-local time while the timeline row above it shows the
// profile-zone time — same underlying UTC instant, two different labels.
const formatDisplayDate = (raw, viewerTimezone) => {
  if (!raw) return '—';
  const { dateLabel } = formatInstantForViewer(raw, viewerTimezone);
  return dateLabel || raw;
};

const formatDisplayDateTime = (raw, viewerTimezone) => {
  if (!raw) return '—';
  const { dateLabel, timeLabel } = formatInstantForViewer(raw, viewerTimezone);
  if (!dateLabel && !timeLabel) return raw;
  return [dateLabel, timeLabel].filter(Boolean).join(' · ');
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') return null;
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(n)) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 2,
  }).format(n);
};

const humaniseStatus = (status) => {
  if (!status) return '—';
  return String(status)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Same 3-way split TransactionTimeline uses for AWAITING_ASSIGNMENT so the
 * header status reads "Provider Not Assigned" for an open SR (requester's
 * turn to assign), "Awaiting Company Assignment" for fresh company routing,
 * and "Company Reassigning" for a post-inspector-decline bounce. Kept in
 * lockstep with TransactionTimeline.js resolveSrStatusLabel — if that
 * mapping changes, update both.
 */
const resolveSrStatusLabel = (sr) => {
  if (!sr) return humaniseStatus(null);
  const status = sr.status || '';
  if (status === 'AWAITING_ASSIGNMENT') {
    if (!sr.providerId && !sr.routedByCompanyId) return 'Provider Not Assigned';
    if (sr.previousProviderId) return 'Company Reassigning';
    return 'Awaiting Company Assignment';
  }
  // Timeline-derived substatus: once the SP marks step_6 (Payment Received)
  // complete but hasn't hit step_7 (Mark Job Complete) yet, the parent SR
  // status is still IN_PROGRESS — surface "Payment Received" instead so
  // the requester can see the SP recorded receipt of payment.
  if (status === 'IN_PROGRESS' && Array.isArray(sr.timeline)) {
    const step6 = sr.timeline.find((e) => e.id === 'step_6');
    const step7 = sr.timeline.find((e) => e.id === 'step_7');
    if (step6?.status === 'COMPLETED' && step7?.status !== 'COMPLETED') {
      return 'Payment Received';
    }
  }
  // Same requester-side wording as the timeline pill — the SP marks the job complete.
  if (status === 'COMPLETED') return 'Vendor marked Complete';
  return humaniseStatus(status);
};

// Split a real ISO instant (backend requestedTime) into the local wall-
// clock <input type="date"> and <input type="time"> values the edit form
// consumes, and combine them back on save. Both directions go through the
// viewer's profile zone (State-derived, same helper the timeline uses)
// rather than the device zone, so the picker opens on the same wall-clock
// the Scheduled read row displays — no silent shift between what you read
// and what you're about to edit.
const isoToDateInputValue = (raw, zone) => instantToWallClockInZone(raw, zone).localDate;
const dateTimeInputsToIso = (dateStr, timeStr, zone) => toUtcInstant(dateStr, timeStr, zone);

// Post-accept lifecycle states — Edit + Assign/Reassign both hide once
// the SP has picked the job up (backend rejects edits on these too).
// DECLINED / CANCELLED / EXPIRED are intentionally NOT here — those are
// terminal-fail states where mutations are still meaningful.
const POST_ACCEPT_STATUSES = new Set(['ACCEPTED', 'IN_PROGRESS', 'COMPLETED']);

const URGENCY_OPTIONS = ['NORMAL', 'URGENT', 'EMERGENCY'];

const ServiceRequestDetailsModal = ({ sr, onClose, onAssignClick, onSave }) => {
  // Same profile-derived zone the transaction timeline uses to render the
  // linked SR's schedule — read once per mount (viewer profile can't change
  // mid-modal).
  const viewerTimezone = getViewerTimezone();
  const [isEditing, setIsEditing] = useState(false);
  const [draftDate, setDraftDate] = useState('');
  const [draftTime, setDraftTime] = useState('');
  const [draftUrgency, setDraftUrgency] = useState('NORMAL');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftEstimate, setDraftEstimate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Final Payment Amount lives on its own card with per-card edit
  // (mirrors the SP Billing tab's Billed Amount pattern) instead of
  // being folded into the header-Edit form — payment recording is a
  // discrete action the requester takes independently of everything
  // else on the SR.
  const [isFinalPaymentEditing, setIsFinalPaymentEditing] = useState(false);
  const [finalPaymentDraft, setFinalPaymentDraft] = useState('');
  const [savingFinalPayment, setSavingFinalPayment] = useState(false);
  const [finalPaymentError, setFinalPaymentError] = useState('');

  // Reset the draft + edit-mode whenever the modal opens on a new SR
  // (sr.id changes) or closes. Prevents a stale draft from an earlier
  // View Details bleeding into the next.
  useEffect(() => {
    if (!sr) {
      setIsEditing(false);
      setSaveError('');
      setIsFinalPaymentEditing(false);
      setFinalPaymentError('');
      return;
    }
    setDraftDate(isoToDateInputValue(sr.requestedTime || sr.requestedDate, viewerTimezone));
    // "Due By Time" input was removed — the deadline is end of the Due By
    // date. Pin the outbound requestedTime to 23:59 so the DTO still gets a
    // full instant when the requester saves an edit.
    setDraftTime('23:59');
    setDraftUrgency(sr.urgencyLevel || 'NORMAL');
    setDraftNotes(sr.description || '');
    setDraftEstimate(
      sr.estimatedAmount === null || sr.estimatedAmount === undefined || sr.estimatedAmount === ''
        ? ''
        : String(sr.estimatedAmount)
    );
    setFinalPaymentDraft(
      sr.finalPaymentAmount === null || sr.finalPaymentAmount === undefined || sr.finalPaymentAmount === ''
        ? ''
        : String(sr.finalPaymentAmount)
    );
    setIsEditing(false);
    setSaveError('');
    setIsFinalPaymentEditing(false);
    setFinalPaymentError('');
  }, [sr?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sr) return null;

  const title = [getServiceTypeLabel(sr), sr.propertyAddress].filter(Boolean).join(' — ') || 'Service Request';
  const estimatedLabel = formatCurrency(sr.estimatedAmount);
  const billedLabel = formatCurrency(sr.billedAmount);
  const finalPaymentLabel = formatCurrency(sr.finalPaymentAmount);
  const preAccept = !POST_ACCEPT_STATUSES.has(sr.status);
  // Reassign predicate lives in a shared helper — see comment there for
  // the (status × routing type) rules. Modal is one of several potential
  // Reassign surfaces; the helper is the single source of truth so a
  // spec change only touches one file per repo.
  const { enabled: reassignEnabled, disabledReason: reassignDisabledReason } =
    getRequesterReassignState(sr);
  const reassignDisabled = !reassignEnabled;
  const showAssignReassign = !!onAssignClick && !isEditing;
  const showEditButton = !!onSave && preAccept && !isEditing;

  const handleSave = async () => {
    if (!onSave) return;
    setSaveError('');
    // Build a partial payload — send only fields that actually changed
    // so the backend's null-means-untouched contract stays honest and
    // audit trails don't fire for no-op writes.
    const payload = {};

    // requestedTime input was removed (Due-By-Time UI drop); the modal now
    // only edits the deadline date. Server accepts null on requestedTime.
    const combinedIso = draftDate ? dateTimeInputsToIso(draftDate, '00:00', viewerTimezone) : null;
    const originalCombined = sr.requestedDate;
    if (combinedIso && combinedIso !== new Date(originalCombined || 0).toISOString()) {
      payload.requestedDate = combinedIso;
    }

    const normalisedUrgency = draftUrgency || 'NORMAL';
    if (normalisedUrgency !== (sr.urgencyLevel || 'NORMAL')) {
      payload.urgencyLevel = normalisedUrgency;
    }

    const trimmedNotes = draftNotes.trim();
    if (trimmedNotes !== (sr.description || '').trim()) {
      payload.description = trimmedNotes;
    }

    const parsedEstimate = draftEstimate.trim() === '' ? null : Number(draftEstimate);
    const originalEstimate = sr.estimatedAmount === null || sr.estimatedAmount === undefined || sr.estimatedAmount === ''
      ? null
      : Number(sr.estimatedAmount);
    if (
      parsedEstimate !== null &&
      Number.isFinite(parsedEstimate) &&
      parsedEstimate >= 0 &&
      parsedEstimate !== originalEstimate
    ) {
      payload.estimatedAmount = parsedEstimate;
    }

    if (Object.keys(payload).length === 0) {
      // Nothing changed — treat as cancel to keep the UX predictable.
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      await onSave(payload);
      setIsEditing(false);
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setDraftDate(isoToDateInputValue(sr.requestedTime || sr.requestedDate, viewerTimezone));
    // "Due By Time" input was removed — the deadline is end of the Due By
    // date. Pin the outbound requestedTime to 23:59 so the DTO still gets a
    // full instant when the requester saves an edit.
    setDraftTime('23:59');
    setDraftUrgency(sr.urgencyLevel || 'NORMAL');
    setDraftNotes(sr.description || '');
    setDraftEstimate(
      sr.estimatedAmount === null || sr.estimatedAmount === undefined || sr.estimatedAmount === ''
        ? ''
        : String(sr.estimatedAmount)
    );
    setSaveError('');
    setIsEditing(false);
  };

  const startFinalPaymentEdit = () => {
    setFinalPaymentDraft(
      sr.finalPaymentAmount === null || sr.finalPaymentAmount === undefined || sr.finalPaymentAmount === ''
        ? ''
        : String(sr.finalPaymentAmount)
    );
    setFinalPaymentError('');
    setIsFinalPaymentEditing(true);
  };

  const cancelFinalPaymentEdit = () => {
    setFinalPaymentError('');
    setIsFinalPaymentEditing(false);
  };

  const saveFinalPayment = async () => {
    if (!onSave) return;
    const parsed = finalPaymentDraft.trim() === '' ? null : Number(finalPaymentDraft);
    if (parsed === null || !Number.isFinite(parsed) || parsed < 0) {
      setFinalPaymentError('Enter a non-negative amount.');
      return;
    }
    setFinalPaymentError('');
    setSavingFinalPayment(true);
    try {
      await onSave({ finalPaymentAmount: parsed });
      setIsFinalPaymentEditing(false);
    } catch (err) {
      setFinalPaymentError(err?.response?.data?.message || err?.message || 'Failed to save.');
    } finally {
      setSavingFinalPayment(false);
    }
  };

  // Portal into document.body so the overlay escapes .main-content's
  // z-index:1 stacking context — otherwise the DashboardHeader's sticky
  // nav (z-index 1001 in the root context) covers the modal regardless of
  // .sr-details-overlay's own z-index. Same pattern as the pre-approval
  // PDF viewer in TransactionTimeline.
  return createPortal(
    <div className="sr-details-overlay" onClick={saving ? undefined : onClose}>
      <div
        className="sr-details-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Service Request Details"
      >
        {/* Header */}
        <div className="sr-details-header">
          <div className="sr-details-header-text">
            <h2 className="sr-details-title">{title}</h2>
            <p className="sr-details-meta">
              Requested {formatDisplayDate(sr.createdAt || sr.requestedDate, viewerTimezone)} · Status: {resolveSrStatusLabel(sr)}
            </p>
          </div>
          {showEditButton && (
            <button
              type="button"
              className="sr-details-edit-header-btn"
              onClick={() => setIsEditing(true)}
              title="Edit service request"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
              </svg>
              Edit
            </button>
          )}
          <button
            className="sr-details-close"
            onClick={onClose}
            aria-label="Close details"
            type="button"
            disabled={saving}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {showAssignReassign && (
          <div className="sr-details-actions">
            <button
              type="button"
              className="sr-details-action sr-details-action--primary"
              onClick={onAssignClick}
              disabled={reassignDisabled}
              aria-disabled={reassignDisabled}
              title={reassignDisabled ? reassignDisabledReason : undefined}
            >
              {sr.providerId ? 'Reassign' : 'Assign'}
            </button>
            {reassignDisabled && (
              <span className="sr-details-action-hint">{reassignDisabledReason}</span>
            )}
          </div>
        )}

        <div className="sr-details-body">
          {/* Amount cards grid — three tiles matching the reference design. */}
          <div className="sr-details-amount-grid">
            <AmountCard
              label="ESTIMATED AMOUNT"
              value={estimatedLabel}
              emptyText="Not provided"
              hint={estimatedLabel
                ? 'As entered on request creation'
                : 'The requester did not add an estimate'}
            />
            <AmountCard
              label="BILLED AMOUNT"
              value={billedLabel}
              emptyText="Not yet recorded"
              hint={billedLabel
                ? 'Read-only — from provider invoice'
                : 'The provider hasn’t submitted an invoice yet'}
            />
            <div className={`sr-details-amount-card${isFinalPaymentEditing ? ' sr-details-amount-card--editing' : ''}`}>
              <div className="sr-details-amount-label">FINAL PAYMENT AMOUNT</div>
              {isFinalPaymentEditing ? (
                <div className="sr-details-amount-edit">
                  <input
                    type="number"
                    className="sr-details-amount-input"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={finalPaymentDraft}
                    onChange={(e) => setFinalPaymentDraft(e.target.value)}
                    disabled={savingFinalPayment}
                    autoFocus
                  />
                  <div className="sr-details-amount-edit-buttons">
                    <button
                      type="button"
                      className="sr-details-action sr-details-action--secondary sr-details-action--sm"
                      onClick={cancelFinalPaymentEdit}
                      disabled={savingFinalPayment}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="sr-details-action sr-details-action--primary sr-details-action--sm"
                      onClick={saveFinalPayment}
                      disabled={savingFinalPayment}
                    >
                      {savingFinalPayment ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  {finalPaymentError && (
                    <div className="sr-details-amount-error">{finalPaymentError}</div>
                  )}
                </div>
              ) : finalPaymentLabel ? (
                <div className="sr-details-amount-value">{finalPaymentLabel}</div>
              ) : (
                <div className="sr-details-amount-empty">Not yet recorded</div>
              )}
              {!isFinalPaymentEditing && onSave && (
                <button
                  type="button"
                  className="sr-details-amount-action"
                  onClick={startFinalPaymentEdit}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                  </svg>
                  {finalPaymentLabel ? 'Edit amount' : 'Add amount'}
                </button>
              )}
              {!isFinalPaymentEditing && !onSave && (
                <div className="sr-details-amount-hint">Editable any time</div>
              )}
            </div>
          </div>

          {/* Request Details — read-only rows OR edit form */}
          <h3 className="sr-details-section-header">Request Details</h3>
          {isEditing ? (
            <div className="sr-details-block sr-details-block--editing">
              <div className="sr-details-row sr-details-row--divider">
                <div className="sr-details-row-label">Service Type</div>
                <div className="sr-details-row-value">{getServiceTypeLabel(sr) || '—'}</div>
              </div>
              <div className="sr-details-row sr-details-row--divider">
                <div className="sr-details-row-label">Provider</div>
                <div className="sr-details-row-value">{sr.providerName || sr.providerEmail || '—'}</div>
              </div>
              <div className="sr-details-row sr-details-row--divider">
                <div className="sr-details-row-label">Due By Date</div>
                <div className="sr-details-row-value">
                  <input
                    type="date"
                    className="sr-details-input"
                    value={draftDate}
                    onChange={(e) => setDraftDate(e.target.value)}
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="sr-details-row sr-details-row--divider">
                <div className="sr-details-row-label">Scheduled</div>
                <div className="sr-details-row-value">
                  {(sr.proposedTime || sr.proposedDate)
                    ? formatDisplayDateTime(sr.proposedTime || sr.proposedDate, viewerTimezone)
                    : '—'}
                </div>
              </div>
              <div className="sr-details-row sr-details-row--divider">
                <div className="sr-details-row-label">Property Address</div>
                <div className="sr-details-row-value">{sr.propertyAddress || '—'}</div>
              </div>
              <div className="sr-details-row sr-details-row--divider">
                <div className="sr-details-row-label">Urgency</div>
                <div className="sr-details-row-value">
                  <select
                    className="sr-details-input sr-details-input--left"
                    value={draftUrgency}
                    onChange={(e) => setDraftUrgency(e.target.value)}
                    disabled={saving}
                  >
                    {URGENCY_OPTIONS.map((u) => (
                      <option key={u} value={u}>{humaniseStatus(u)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sr-details-row sr-details-row--divider">
                <div className="sr-details-row-label">Estimated Amount</div>
                <div className="sr-details-row-value">
                  <input
                    type="number"
                    className="sr-details-input"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={draftEstimate}
                    onChange={(e) => setDraftEstimate(e.target.value)}
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="sr-details-row">
                <div className="sr-details-row-label">Notes</div>
                <div className="sr-details-row-value">
                  <textarea
                    className="sr-details-input sr-details-input--textarea"
                    rows={3}
                    value={draftNotes}
                    onChange={(e) => setDraftNotes(e.target.value)}
                    disabled={saving}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="sr-details-block">
              <DetailRow label="Service Type" value={getServiceTypeLabel(sr) || '—'} />
              <DetailRow label="Provider" value={sr.providerName || sr.providerEmail || '—'} />
              <DetailRow label="Due By Date" value={formatDisplayDate(sr.requestedDate, viewerTimezone)} />
              <DetailRow
                label="Scheduled"
                value={(sr.proposedTime || sr.proposedDate)
                  ? formatDisplayDateTime(sr.proposedTime || sr.proposedDate, viewerTimezone)
                  : '—'}
              />
              <DetailRow label="Property Address" value={sr.propertyAddress || '—'} />
              <DetailRow label="Urgency" value={humaniseStatus(sr.urgencyLevel)} />
              <DetailRow label="Notes" value={sr.description || '—'} isLast />
            </div>
          )}

          {isEditing && (
            <div className="sr-details-edit-footer">
              {saveError && <div className="sr-details-edit-error">{saveError}</div>}
              <div className="sr-details-edit-footer-buttons">
                <button
                  type="button"
                  className="sr-details-action sr-details-action--secondary"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="sr-details-action sr-details-action--primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const DetailRow = ({ label, value, isLast }) => (
  <div className={`sr-details-row${isLast ? '' : ' sr-details-row--divider'}`}>
    <div className="sr-details-row-label">{label}</div>
    <div className="sr-details-row-value">{value}</div>
  </div>
);

const AmountCard = ({ label, value, emptyText, hint }) => (
  <div className="sr-details-amount-card">
    <div className="sr-details-amount-label">{label}</div>
    {value ? (
      <div className="sr-details-amount-value">{value}</div>
    ) : (
      <div className="sr-details-amount-empty">{emptyText}</div>
    )}
    <div className="sr-details-amount-hint">{hint}</div>
  </div>
);

export default ServiceRequestDetailsModal;
